import { useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Minus, Plus, Trash2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/contexts/CartContext";
import { CAFE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_MAP = { 0: "Sunday", 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday" };

function parseHours(h) {
  if (!h) return null;
  const [openStr, closeStr] = h.time.split(" – ");
  const [oh, om] = openStr.split(":").map(Number);
  const [ch, cm] = closeStr.split(":").map(Number);
  return { openMin: oh * 60 + om, closeMin: ch * 60 + cm };
}

function fmt(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function buildSlots() {
  const now = new Date();
  const today = CAFE.hours.find((h) => h.day === DAY_MAP[now.getDay()]);
  const t = parseHours(today);
  if (!t) return [];
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const earliest = Math.max(nowMin + 20, t.openMin + 15);
  const start = Math.ceil(earliest / 15) * 15;
  const stopBy = t.closeMin - 15;
  const slots = [];
  for (let m = start; m <= stopBy; m += 15) slots.push(fmt(m));
  return slots;
}

const fieldCls =
  "w-full border-0 border-b border-[var(--line)] bg-transparent py-3 font-body text-base text-[var(--text)] placeholder:text-[var(--text)]/35 outline-none transition-colors duration-300 focus:border-accent";

export default function CartDrawer() {
  const { items, total, setQty, remove, clear, open, setOpen } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", pickup: "ASAP", notes: "" });
  const [loading, setLoading] = useState(false);
  const slots = useMemo(() => buildSlots(), [open]); // rebuild when opened

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return toast.error("Your order is empty.");
    if (!form.name || !form.phone) return toast.error("Please add your name and phone.");
    setLoading(true);
    try {
      await axios.post(`${API}/orders`, {
        name: form.name,
        phone: form.phone,
        pickup_time: form.pickup,
        notes: form.notes,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty })),
        total: total.toFixed(2),
      });
      toast.success("Order received! We'll have it ready — pay at the counter when you arrive.");
      clear();
      setForm({ name: "", phone: "", pickup: "ASAP", notes: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="flex w-full flex-col overflow-y-auto border-l border-[var(--line)] bg-surface text-[var(--text)] sm:max-w-md"
      >
        <SheetHeader className="border-b border-[var(--line)] pb-4 text-left">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-accent">Your Order</p>
          <SheetTitle className="font-display text-3xl tracking-tight text-[var(--text)]">Pickup at Mehretina</SheetTitle>
          <p className="font-body text-xs text-[var(--text)]/50">Pay at the counter when you arrive.</p>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <p className="font-serif-c text-2xl italic text-[var(--text)]/60">Your order is empty.</p>
            <p className="mt-2 font-body text-sm text-[var(--text)]/40">Add something from the menu to get started.</p>
          </div>
        ) : (
          <>
            <ul data-testid="cart-items" className="mt-6 flex flex-col gap-4">
              {items.map((it) => (
                <li key={it.id} data-testid={`cart-item-${it.id}`} className="flex items-start justify-between gap-4 border-b border-[var(--line)] pb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-serif-c text-lg text-[var(--text)]">{it.name}</p>
                    <p className="mt-1 font-body text-xs text-[var(--text)]/50">${it.price} each</p>
                    <div className="mt-3 inline-flex items-center gap-3 rounded-full border border-[var(--line)] px-1 py-1">
                      <button
                        type="button"
                        data-testid={`cart-dec-${it.id}`}
                        onClick={() => setQty(it.id, it.qty - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--text)]/70 hover:bg-black/30"
                        aria-label="Decrease"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="min-w-4 text-center font-body text-sm">{it.qty}</span>
                      <button
                        type="button"
                        data-testid={`cart-inc-${it.id}`}
                        onClick={() => setQty(it.id, it.qty + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--text)]/70 hover:bg-black/30"
                        aria-label="Increase"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="font-display text-lg text-accent">${(parseFloat(it.price) * it.qty).toFixed(2)}</span>
                    <button
                      type="button"
                      data-testid={`cart-remove-${it.id}`}
                      onClick={() => remove(it.id)}
                      className="text-[var(--text)]/40 transition-colors duration-200 hover:text-accent"
                      aria-label="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-baseline justify-between border-t border-[var(--line)] pt-4">
              <span className="font-body text-xs uppercase tracking-[0.25em] text-[var(--text)]/60">Subtotal</span>
              <span data-testid="cart-total" className="font-display text-2xl text-[var(--text)]">${total.toFixed(2)}</span>
            </div>

            <form onSubmit={submit} data-testid="order-form" className="mt-6 flex flex-col gap-6">
              <input data-testid="order-name" className={fieldCls} placeholder="Full name" value={form.name} onChange={setField("name")} />
              <input data-testid="order-phone" className={fieldCls} placeholder="Phone" value={form.phone} onChange={setField("phone")} />

              <div>
                <p className="mb-2 font-body text-xs uppercase tracking-[0.2em] text-accent">Pickup time</p>
                <Select value={form.pickup} onValueChange={(v) => setForm((f) => ({ ...f, pickup: v }))}>
                  <SelectTrigger data-testid="order-pickup" className={`${fieldCls} h-auto rounded-none`}>
                    <SelectValue placeholder="Pickup time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 border-[var(--line)] bg-surface text-[var(--text)]">
                    <SelectItem value="ASAP">As soon as possible (~15 min)</SelectItem>
                    {slots.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {slots.length === 0 && (
                  <p className="mt-2 font-body text-xs text-[var(--text)]/50">
                    We're closed right now — please order during opening hours.
                  </p>
                )}
              </div>

              <textarea
                data-testid="order-notes"
                rows={2}
                className={`${fieldCls} resize-none`}
                placeholder="Notes (allergies, extras, milk choice…)"
                value={form.notes}
                onChange={setField("notes")}
              />

              <button
                type="submit"
                data-testid="order-submit"
                disabled={loading}
                className="mt-2 flex items-center justify-center gap-3 rounded-full bg-accent px-8 py-4 font-body text-sm uppercase tracking-[0.2em] text-[var(--text)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Placing…" : `Place Order · $${total.toFixed(2)}`}
              </button>
              <p className="text-center font-body text-xs text-[var(--text)]/40">
                <X size={10} className="mr-1 inline" />
                No payment now — pay at the counter on pickup.
              </p>
            </form>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
